import { useCallback } from 'react'
import {
  X, Columns2, Rows2, Maximize2, Minimize2, ExternalLink, Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { VIEW_REGISTRY, NAV_ITEMS, getViewPath } from '@/lib/workspace/registry'
import type { PanelNode } from '@/lib/workspace/types'
import { panelActionsHostId } from './panelActionsHost'

interface PanelHeaderProps {
  panel: PanelNode
  isFocused: boolean
  isMaximized: boolean
  /** When true (mobile), hide split + maximize + pop-out controls. */
  compact?: boolean
}

export function PanelHeader({ panel, isFocused, isMaximized, compact }: PanelHeaderProps) {
  const setActiveView = useWorkspaceStore((s) => s.setActiveView)
  const closeView = useWorkspaceStore((s) => s.closeView)
  const splitPanel = useWorkspaceStore((s) => s.splitPanel)
  const maximizePanel = useWorkspaceStore((s) => s.maximizePanel)
  const restoreLayout = useWorkspaceStore((s) => s.restoreLayout)
  const closePanel = useWorkspaceStore((s) => s.closePanel)
  const addViewToPanel = useWorkspaceStore((s) => s.addViewToPanel)

  const handleSplitRight = useCallback(() => splitPanel(panel.id, 'horizontal'), [panel.id, splitPanel])
  const handleSplitDown  = useCallback(() => splitPanel(panel.id, 'vertical'),   [panel.id, splitPanel])

  const handleMaximize = useCallback(() => {
    if (isMaximized) restoreLayout()
    else maximizePanel(panel.id)
  }, [isMaximized, panel.id, maximizePanel, restoreLayout])

  const handlePopOut = useCallback(() => {
    const active = panel.views.find((v) => v.instanceId === panel.activeViewInstanceId)
    if (!active) return
    const path = getViewPath(active.viewId, active.params)
    const w = Math.round(window.screen.availWidth * 0.6)
    const h = Math.round(window.screen.availHeight * 0.7)
    const left = Math.round((window.screen.availWidth - w) / 2)
    const top = Math.round((window.screen.availHeight - h) / 2)
    window.open(window.location.origin + path, '_blank', `width=${w},height=${h},left=${left},top=${top}`)
  }, [panel.views, panel.activeViewInstanceId])

  return (
    <div
      className={cn(
        'flex h-9 items-center border-b border-border bg-muted/20 transition-colors',
        isFocused && 'border-t-2 border-t-primary',
        !isFocused && 'border-t-2 border-t-transparent',
      )}
    >
      {/* Tabs */}
      <div className="flex min-w-[6.5rem] flex-1 items-center gap-0 overflow-x-auto scrollbar-thin">
        {panel.views.map((view) => {
          const def = VIEW_REGISTRY[view.viewId]
          if (!def) return null
          const isActive = view.instanceId === panel.activeViewInstanceId
          const Icon = def.icon
          return (
            <button
              key={view.instanceId}
              onClick={() => setActiveView(panel.id, view.instanceId)}
              className={cn(
                'group flex h-9 items-center gap-1.5 border-r border-border px-3 text-xs font-mono whitespace-nowrap min-w-0',
                isActive
                  ? 'bg-background text-foreground'
                  : 'text-muted-foreground hover:bg-background/50 hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="max-w-[140px] truncate">{def.title}</span>
              {panel.views.length > 1 && (
                <span
                  onClick={(e) => { e.stopPropagation(); closeView(panel.id, view.instanceId) }}
                  className="ml-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm hover:bg-destructive/20 hover:text-destructive"
                  role="button"
                  aria-label="Close tab"
                >
                  <X className="h-3 w-3" />
                </span>
              )}
            </button>
          )
        })}
        {/* Add-tab menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex h-9 w-9 items-center justify-center text-muted-foreground hover:bg-background/50 hover:text-foreground border-r border-border"
              title="Add tab"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Add view to this panel</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {NAV_ITEMS.map((v) => {
              const Icon = v.icon
              return (
                <DropdownMenuItem
                  key={v.id}
                  onSelect={() => addViewToPanel(panel.id, v.id)}
                >
                  <Icon className="h-4 w-4" /> {v.title}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Page actions (ported) plus desktop panel controls.
          The host stays on the phone — split / maximize / pop-out do not. */}
      <div className="flex max-w-[58%] min-w-0 items-center px-1">
        <div
          id={panelActionsHostId(panel.id)}
          className="flex h-9 min-w-0 items-center gap-1 overflow-x-auto flex-nowrap scrollbar-thin empty:hidden [&>*]:shrink-0 [&_button]:h-7"
        />
        {!compact && (
          <div className="flex shrink-0 items-center gap-0">
            <Ctl icon={Columns2}  label="Split right" onClick={handleSplitRight} />
            <Ctl icon={Rows2}     label="Split down"  onClick={handleSplitDown} />
            <Ctl
              icon={isMaximized ? Minimize2 : Maximize2}
              label={isMaximized ? 'Restore' : 'Maximize'}
              onClick={handleMaximize}
            />
            <Ctl icon={ExternalLink} label="Pop out" onClick={handlePopOut} />
            <Ctl icon={X} label="Close panel" onClick={() => closePanel(panel.id)} className="hover:text-destructive" />
          </div>
        )}
      </div>
    </div>
  )
}

function Ctl({
  icon: Icon, label, onClick, className,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  onClick: () => void
  className?: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          className={cn(
            'h-7 w-7 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors',
            className,
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  )
}
